import {
  FuzzyNameMatchThreshold,
  isFuzzyNameMatch,
  matchNameFields,
  NameMatchCheck,
  nameMatchCheck,
} from "@/src/lib/name-matching/match";
import { normalizePhoneNumber } from "@/src/models/common-schema";
import type { Patient } from "@/src/models/patient";

export interface PatientMatchingOptions {
  /**
   * Voice identity lookup: besides misentries, accept homophones, nicknames and their combinations, each only
   * with the identity evidence it requires.
   */
  matchSpokenNameVariants?: boolean;
}

type PatientIdentity = Pick<
  Patient,
  | "givenName"
  | "middleName"
  | "familyName"
  | "dateOfBirth"
  | "mobilePhoneNumber"
  | "homePhoneNumber"
  | "workPhoneNumber"
>;

export function getPatientIdentityPhones(patient: PatientIdentity): string[] {
  return [
    ...new Set(
      [patient.mobilePhoneNumber, patient.homePhoneNumber, patient.workPhoneNumber]
        .map(normalizePhoneNumber)
        .filter((phone): phone is string => phone !== null)
    ),
  ];
}

function sameDateOfBirth(candidate: PatientIdentity, query: PatientIdentity): boolean {
  return Boolean(query.dateOfBirth) && candidate.dateOfBirth === query.dateOfBirth;
}

function sharesPhone(candidate: PatientIdentity, query: PatientIdentity): boolean {
  const queryPhones = getPatientIdentityPhones(query);

  return getPatientIdentityPhones(candidate).some((phone) => queryPhones.includes(phone));
}

function phonesConflict(candidate: PatientIdentity, query: PatientIdentity): boolean {
  const candidatePhones = getPatientIdentityPhones(candidate);

  return candidatePhones.length > 0 && getPatientIdentityPhones(query).length > 0 && !sharesPhone(candidate, query);
}

/**
 * Name evidence for a record sharing the caller's DOB and a phone number. Every check and combination
 * qualifies; a misentry alone may use the Loose threshold, but inside a combination it must meet Strict.
 * Callers must still reject several distinct qualifying charts.
 */
export function matchSpokenNameWithPhoneAndDob(
  candidate: PatientIdentity,
  query: PatientIdentity
): NameMatchCheck | null {
  if (!sameDateOfBirth(candidate, query) || !sharesPhone(candidate, query)) return null;

  const fields = matchNameFields(candidate, query);

  if (fields) return nameMatchCheck(fields);

  return isFuzzyNameMatch(candidate, query, FuzzyNameMatchThreshold.Loose) ? NameMatchCheck.Misentry : null;
}

/**
 * Name evidence for a record sharing only the caller's DOB; callers must find it unique in the group's complete
 * same-DOB set. Misentries qualify at the Strict threshold, as they do for every source. Homophones qualify
 * unless both records carry phones and none is shared. Nicknames and combinations require a shared phone.
 */
export function matchSpokenNameWithDob(candidate: PatientIdentity, query: PatientIdentity): NameMatchCheck | null {
  if (!sameDateOfBirth(candidate, query)) return null;

  const fields = matchNameFields(candidate, query);
  const check = fields && nameMatchCheck(fields);

  if (check === NameMatchCheck.Exact) return check;
  if (check === NameMatchCheck.Homophone && !phonesConflict(candidate, query)) return check;

  return isFuzzyNameMatch(candidate, query, FuzzyNameMatchThreshold.Strict) ? NameMatchCheck.Misentry : null;
}
